import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Row, Col, Table, Form, Modal } from 'react-bootstrap';
import { FaKey, FaDesktop, FaSyncAlt, FaCopy, FaCheckCircle, FaClock, FaFlask, FaCalendarAlt } from 'react-icons/fa';
import axios from 'axios';
import { useLicense } from '../../context/LicenseContext';
import LicenseStatus from '../license/LicenseStatus';

const LicenseManagement = () => {
  const { licenseStatus, refreshLicenseStatus } = useLicense();
  const [machineInfo, setMachineInfo] = useState(null);
  const [queuedLicenses, setQueuedLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testDate, setTestDate] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [currentDate, setCurrentDate] = useState(null);

  useEffect(() => {
    fetchMachineInfo();
    fetchQueuedLicenses();
    fetchCurrentDate();
  }, []);

  const fetchMachineInfo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/license/machine-info', {
        headers: { 'x-auth-token': token }
      });
      setMachineInfo(response.data);
    } catch (err) {
      console.error('Error fetching machine info:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueuedLicenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/license/queue', {
        headers: { 'x-auth-token': token }
      });
      setQueuedLicenses(response.data);
    } catch (err) {
      console.error('Error fetching queued licenses:', err);
    }
  };

  const fetchCurrentDate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/license/current-date', {
        headers: { 'x-auth-token': token }
      });
      setCurrentDate(new Date(response.data.currentDate));
    } catch (err) {
      console.error('Error fetching current date:', err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const processQueue = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/license/process-queue', {}, {
        headers: { 'x-auth-token': token }
      });
      refreshLicenseStatus();
      fetchQueuedLicenses();
    } catch (err) {
      console.error('Error processing queue:', err);
    }
  };

  const enableTestMode = async () => {
    if (!testDate) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/license/test-mode', {
        testDate: new Date(testDate).toISOString()
      }, {
        headers: { 'x-auth-token': token }
      });
      
      setTestMode(true);
      setShowTestModal(false);
      refreshLicenseStatus();
      fetchCurrentDate();
    } catch (err) {
      console.error('Error enabling test mode:', err);
    }
  };

  const disableTestMode = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/license/test-mode', {
        headers: { 'x-auth-token': token }
      });
      
      setTestMode(false);
      refreshLicenseStatus();
      fetchCurrentDate();
    } catch (err) {
      console.error('Error disabling test mode:', err);
    }
  };

  const generateTestLicense = async (minutes) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/license/generate-test-license', {
        durationMinutes: minutes
      }, {
        headers: { 'x-auth-token': token }
      });
      
      alert(`Test License Generated:\\n\\n${response.data.licenseKey}\\n\\nDuration: ${minutes} minutes\\n\\nUse this for testing license expiration.`);
    } catch (err) {
      console.error('Error generating test license:', err);
    }
  };

  return (
    <div className="license-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FaKey className="me-2" />
          License Management
        </h2>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-info"
            size="sm"
            onClick={() => setShowTestModal(true)}
          >
            <FaFlask className="me-1" />
            Test Mode
          </Button>
          <Button 
            variant="outline-primary"
            onClick={() => {
              refreshLicenseStatus();
              fetchMachineInfo();
              fetchQueuedLicenses();
              fetchCurrentDate();
            }}
            disabled={loading}
          >
            <FaSyncAlt className={`me-1 ${loading ? 'fa-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Test Mode Indicator */}
      {testMode && (
        <Alert variant="warning" className="mb-3">
          <FaFlask className="me-2" />
          <strong>Test Mode Active</strong> - System date is being overridden for testing purposes.
          <Button variant="outline-warning" size="sm" className="ms-3" onClick={disableTestMode}>
            Disable Test Mode
          </Button>
        </Alert>
      )}

      {/* Current Date Display */}
      {currentDate && (
        <Alert variant="info" className="mb-3">
          <FaCalendarAlt className="me-2" />
          <strong>System Date:</strong> {currentDate.toLocaleString()}
          {testMode && ' (Test Override)'}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          {/* License Status Card */}
          <LicenseStatus showDetailed={true} />
          
          {/* Queued Licenses Card */}
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <FaClock className="me-2" />
                Queued Licenses
              </h6>
              <Button variant="outline-primary" size="sm" onClick={processQueue}>
                Process Queue
              </Button>
            </Card.Header>
            <Card.Body>
              {queuedLicenses.length > 0 ? (
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Duration</th>
                      <th>Queued Date</th>
                      <th>Status</th>
                      <th>Activation Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queuedLicenses.map((license, index) => (
                      <tr key={index}>
                        <td>
                          <Badge bg={license.LicenseType === 'TRIAL' ? 'info' : 'success'}>
                            {license.LicenseType}
                          </Badge>
                        </td>
                        <td>{license.Duration} days</td>
                        <td>{new Date(license.QueuedDate).toLocaleDateString()}</td>
                        <td>
                          <Badge bg={license.IsActivated ? 'success' : 'warning'}>
                            {license.IsActivated ? 'Activated' : 'Pending'}
                          </Badge>
                        </td>
                        <td>
                          {license.ActivationDate 
                            ? new Date(license.ActivationDate).toLocaleDateString()
                            : 'When current expires'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-3">
                  <FaClock size={32} className="mb-2 opacity-50" />
                  <div>No licenses queued</div>
                  <small>Future license keys will appear here when entered early</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          {/* Machine Information Card */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FaDesktop className="me-2" />
                Machine Information
              </h6>
            </Card.Header>
            <Card.Body>
              {machineInfo ? (
                <div>
                  <Table size="sm" className="mb-3">
                    <tbody>
                      <tr>
                        <td><strong>Hostname:</strong></td>
                        <td>{machineInfo.systemInfo.hostname}</td>
                      </tr>
                      <tr>
                        <td><strong>Platform:</strong></td>
                        <td>{machineInfo.systemInfo.platform}</td>
                      </tr>
                      <tr>
                        <td><strong>Architecture:</strong></td>
                        <td>{machineInfo.systemInfo.arch}</td>
                      </tr>
                      <tr>
                        <td><strong>CPU Cores:</strong></td>
                        <td>{machineInfo.systemInfo.cpuCount}</td>
                      </tr>
                      <tr>
                        <td><strong>Memory:</strong></td>
                        <td>{machineInfo.systemInfo.totalMemory}</td>
                      </tr>
                    </tbody>
                  </Table>
                  
                  <div className="mb-2">
                    <strong>Machine Fingerprint:</strong>
                  </div>
                  <div className="d-flex align-items-center">
                    <code className="flex-grow-1 bg-light p-2 rounded small">
                      {machineInfo.machineFingerprint.substring(0, 16)}...
                    </code>
                    <Button 
                      variant="outline-secondary"
                      size="sm"
                      className="ms-2"
                      onClick={() => copyToClipboard(machineInfo.machineFingerprint)}
                    >
                      {copied ? <FaCheckCircle className="text-success" /> : <FaCopy />}
                    </Button>
                  </div>
                  <small className="text-muted">
                    This fingerprint is used to bind the license to this specific machine.
                  </small>
                </div>
              ) : (
                <div className="text-center">
                  {loading ? (
                    <div>
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Loading machine info...
                    </div>
                  ) : (
                    <div>
                      <p className="text-muted mb-2">Machine information not available</p>
                      <Button variant="outline-primary" size="sm" onClick={fetchMachineInfo}>
                        Retry
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
          
          {/* Testing Tools Card */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FaFlask className="me-2" />
                Testing Tools
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={() => generateTestLicense(5)}
                >
                  Generate 5-min Test License
                </Button>
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={() => generateTestLicense(60)}
                >
                  Generate 1-hour Test License
                </Button>
                <Button 
                  variant="outline-warning" 
                  size="sm"
                  onClick={() => setShowTestModal(true)}
                >
                  Set Test Date
                </Button>
              </div>
              <small className="text-muted mt-2 d-block">
                Use these tools to test license expiration without waiting days.
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* License Information */}
      <Card>
        <Card.Header>
          <h6 className="mb-0">License Information & Queue System</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6>License Types</h6>
              <Table size="sm" className="mb-3">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Duration</th>
                    <th>Key Format</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><Badge bg="info">Trial</Badge></td>
                    <td>60 days</td>
                    <td><code>TR-XXXX-...</code></td>
                  </tr>
                  <tr>
                    <td><Badge bg="success">Standard</Badge></td>
                    <td>6 months</td>
                    <td><code>ST-XXXX-...</code></td>
                  </tr>
                  <tr>
                    <td><Badge bg="warning">Test</Badge></td>
                    <td>5min-24hr</td>
                    <td><code>TE-XXXX-...</code></td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            
            <Col md={6}>
              <h6>Queue System Benefits</h6>
              <div className="bg-light p-3 rounded">
                <ul className="mb-0 small">
                  <li>Enter renewal licenses early</li>
                  <li>No service interruption when licenses expire</li>
                  <li>Automatic activation of queued licenses</li>
                  <li>Multiple licenses can be queued in advance</li>
                  <li>Perfect timing every renewal</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Test Mode Modal */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFlask className="me-2" />
            Test Mode Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Test Date & Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={testDate}
                onChange={(e) => setTestDate(e.target.value)}
              />
              <Form.Text className="text-muted">
                Set a future date to simulate license expiration
              </Form.Text>
            </Form.Group>
            
            <Alert variant="warning">
              <strong>⚠️ Warning:</strong> Test mode overrides the system date for license calculations. 
              Use only for testing license expiration scenarios.
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={enableTestMode} disabled={!testDate}>
            Enable Test Mode
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LicenseManagement;
